/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: EmergencyNumbersConfigPage.tsx
 🗓️ Created: 2026-03-18
 🧩 Description:
    Unified Emergency Numbers configuration page — VitalPBX style.
    General settings (outbound route, emails) + dynamic list of numbers.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Plus, Trash2, Search, ChevronDown } from 'lucide-react';

import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { api } from '../../lib/api';

// ─── Searchable Dropdown ─────────────────────────────────────────────────────
interface ComboSelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: { id: string; name: string }[];
  placeholder?: string;
}

function ComboSelect({ id, value, onChange, options, placeholder = 'Select...' }: ComboSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.id === value);
  const filtered = search.trim()
    ? options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    setOpen(o => !o);
    if (!open) setTimeout(() => searchRef.current?.focus(), 60);
  };

  const handlePick = (optId: string) => {
    onChange(optId);
    setOpen(false);
    setSearch('');
  };

  const SELECT_CLASS = 'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring cursor-pointer';

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Trigger */}
      <button
        id={id}
        type="button"
        onClick={handleToggle}
        className={SELECT_CLASS}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
          {selected ? selected.name : `— ${placeholder} —`}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Floating panel */}
      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1 rounded-md border border-border bg-popover shadow-lg">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          {/* Options */}
          <div className="max-h-52 overflow-y-auto py-1">
            <button
              type="button"
              onClick={() => handlePick('')}
              className="w-full px-3 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
            >
              — {placeholder} —
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No results</div>
            ) : (
              filtered.map(o => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => handlePick(o.id)}
                  className={`w-full px-3 py-1.5 text-left text-sm hover:bg-accent transition-colors ${
                    o.id === value ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'
                  }`}
                >
                  {o.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface NumberRow {
  id: string | null;
  uuid?: string;  // External UUID (replaces Sqids)
  number: string;
  serviceName: string;
  deleted: boolean;
}

interface SharedSettings {
  outboundRouteId: string;
  notifyEmails: string;
  description: string;
}

const defaultShared: SharedSettings = {
  outboundRouteId: '',
  notifyEmails: '',
  description: '',
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EmergencyNumbersConfigPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shared, setShared] = useState<SharedSettings>(defaultShared);
  const [originalShared, setOriginalShared] = useState<SharedSettings>(defaultShared);
  const [rows, setRows] = useState<NumberRow[]>([]);
  const [originalRows, setOriginalRows] = useState<string>('[]');
  const [outboundRoutes, setOutboundRoutes] = useState<{ id: string; name: string }[]>([]);
  const newRowRef = useRef<HTMLInputElement | null>(null);

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadAll = async () => {
      try {
        setLoading(true);

        // Use api.get() for both to avoid fetch/auth inconsistencies
        const [numRes, routeRes] = await Promise.all([
          api.get('/api/telephony/emergency-numbers?limit=100&sortBy=number&sortOrder=asc'),
          api.get('/api/telephony/outbound-routes?limit=200&sortBy=name&sortOrder=asc'),
        ]);

        // Outbound routes: service returns { success:true, data:[...], pagination:{} }
        const routePayload = routeRes.data;
        if (routePayload?.success && Array.isArray(routePayload.data)) {
          setOutboundRoutes(routePayload.data.map((r: { id: number | string; name: string }) => ({ id: String(r.id), name: r.name })));
        }

        // Emergency numbers
        const numbers = (numRes.data?.data ?? []) as Array<{ id: number | string; number: string; description?: string | null; outboundRouteId?: number | null; notifyEmails?: string | null }>;
        const mapped: NumberRow[] = numbers.map((n) => ({
          id: String(n.id),
          number: n.number,
          serviceName: n.description ?? '',
          deleted: false,
        }));
        setRows(mapped.length > 0 ? mapped : [{ id: null, number: '', serviceName: '', deleted: false }]);
        setOriginalRows(JSON.stringify(mapped));

        // Shared settings from first record
        if (numbers.length > 0) {
          const first = numbers[0];
          const s: SharedSettings = {
            outboundRouteId: first.outboundRouteId != null ? String(first.outboundRouteId) : '',
            notifyEmails: first.notifyEmails ?? '',
            description: first.description ?? '',
          };
          setShared(s);
          setOriginalShared(s);
        }
      } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to load emergency numbers');
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const addRow = () => {
    setRows(prev => [...prev, { id: null, number: '', serviceName: '', deleted: false }]);
    setTimeout(() => newRowRef.current?.focus(), 50);
  };

  const updateRow = (index: number, field: keyof Omit<NumberRow, 'id' | 'deleted'>, value: string) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const deleteRow = (index: number) => {
    setRows(prev => prev.map((r, i) => i === index ? { ...r, deleted: true } : r));
  };

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const visibleRows = rows.filter(r => !r.deleted);
    if (visibleRows.some(r => !r.number.trim())) {
      showError('All emergency number fields are required.');
      return;
    }

    try {
      setSaving(true);

      // Send outboundRouteId as-is (Sqid string or empty string → null)
      // The backend z.preprocess with decodeId handles Sqids automatically
      const outboundRouteId = shared.outboundRouteId || null;

      console.log('[EmergencyNumbers] Saving with outboundRouteId:', outboundRouteId, 'raw:', shared.outboundRouteId);


      const buildPayload = (row: NumberRow) => ({
        number: row.number.trim(),
        description: row.serviceName.trim() || null,
        countryCode: 'US',
        outboundRouteId,
        notifyEmails: shared.notifyEmails || null,
        notifyOnCall: !!(shared.notifyEmails),
        enabled: true,
      });

      // 1. Delete marked rows
      const toDelete = rows.filter(r => r.deleted && r.id);
      await Promise.all(toDelete.map(r => api.delete(`/api/telephony/emergency-numbers/${r.uuid ?? r.id}`)));

      // 2. Create / Update rows
      for (const row of visibleRows) {
        if (row.id) {
          await api.put(`/api/telephony/emergency-numbers/${row.uuid ?? row.id}`, buildPayload(row));
        } else {
          await api.post('/api/telephony/emergency-numbers', buildPayload(row));
        }
      }

      showSuccess(t('emergencyNumbers:messages.updateSuccess', 'Emergency numbers saved successfully'));

      // Refresh rows to pick up server-assigned ids
      const refreshRes = await api.get('/api/telephony/emergency-numbers?limit=100&sortBy=number&sortOrder=asc');
      const refreshed = (refreshRes.data?.data ?? []) as Array<{ id: number | string; number: string; description?: string | null }>;
      const refreshedMapped: NumberRow[] = refreshed.map((n) => ({
        id: String(n.id),
        number: n.number,
        serviceName: n.description ?? '',
        deleted: false,
      }));
      setRows(refreshedMapped.length > 0 ? refreshedMapped : [{ id: null, number: '', serviceName: '', deleted: false }]);
      setOriginalRows(JSON.stringify(refreshedMapped));
      setOriginalShared({ ...shared });

    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save';
      console.error('[EmergencyNumbers] Save error:', err);
      showError(msg);
    } finally {
      setSaving(false);
    }
  }, [rows, shared, showSuccess, showError, t]);

  const handleCancel = useCallback(() => {
    setShared({ ...originalShared });
    setRows(JSON.parse(originalRows));
  }, [originalShared, originalRows]);

  const visibleRows = rows.filter(r => !r.deleted);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common:loading')}</div>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <ModuleFormHeader
        icon={<Phone className="h-5 w-5" />}
        title={t('emergencyNumbers:configTitle', 'Emergency Numbers')}
        backButtonText={t('common:back', 'Back')}
        onBack={() => navigate(-1)}
      />

      <div className="space-y-4 mt-8">
        {/* ── GENERAL ── */}
        <FormBox title={t('emergencyNumbers:sections.general', 'General')} icon={<Phone className="h-5 w-5" />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-start">

            {/* Description */}
            <SimpleTooltip content="A short description for this emergency configuration" title="Description" position="top" maxWidth="300px">
              <Label htmlFor="description" className="text-left pt-1">
                {t('emergencyNumbers:fields.description', 'Description')}
              </Label>
            </SimpleTooltip>
            <div>
              <Input
                id="description"
                value={shared.description}
                onChange={(e) => setShared(p => ({ ...p, description: e.target.value }))}
                placeholder="Emergency routing config"
              />
            </div>

            {/* Notification Emails */}
            <SimpleTooltip content="Email addresses to notify when an emergency call is placed (comma-separated)" title="Notification Emails" position="top" maxWidth="300px">
              <Label htmlFor="notifyEmails" className="text-left pt-1">
                {t('emergencyNumbers:fields.notifyEmails', 'Notification Emails')}
              </Label>
            </SimpleTooltip>
            <div>
              <Input
                id="notifyEmails"
                value={shared.notifyEmails}
                onChange={(e) => setShared(p => ({ ...p, notifyEmails: e.target.value }))}
                placeholder="admin@company.com, security@company.com"
              />
            </div>

            {/* Outbound Route — searchable combobox */}
            <SimpleTooltip content="Outbound Route used to route emergency calls to the PSTN" title={t('emergencyNumbers:fields.outboundRouteId', 'Outbound Route')} position="top" maxWidth="300px">
              <Label htmlFor="outboundRouteId" className="text-left pt-1">
                {t('emergencyNumbers:fields.outboundRouteId', 'Outbound Route')}
              </Label>
            </SimpleTooltip>
            <ComboSelect
              id="outboundRouteId"
              value={shared.outboundRouteId}
              onChange={(v) => setShared(p => ({ ...p, outboundRouteId: v }))}
              options={outboundRoutes}
              placeholder={t('emergencyNumbers:placeholders.outboundRouteId', 'Select Outbound Route')}
            />

          </div>
        </FormBox>

        {/* ── LIST OF EMERGENCY NUMBERS ── */}
        <FormBox title={t('emergencyNumbers:sections.numberList', 'List of Emergency Numbers')} icon={<Phone className="h-5 w-5" />}>
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_1fr_auto] gap-3 px-2 pb-1 border-b border-border">
              <span className="text-sm font-medium text-muted-foreground text-center">
                {t('emergencyNumbers:columns.number', 'Emergency Number')}
              </span>
              <span className="text-sm font-medium text-muted-foreground text-center">
                {t('emergencyNumbers:columns.serviceName', 'Service Name')}
              </span>
              <span className="w-8" />
            </div>

            {visibleRows.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t('emergencyNumbers:noNumbers', 'No emergency numbers. Click Add to create one.')}
              </p>
            )}

            {rows.map((row, index) =>
              row.deleted ? null : (
                <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center">
                  <Input
                    ref={row.id === null && index === rows.length - 1 ? newRowRef : undefined}
                    value={row.number}
                    onChange={(e) => updateRow(index, 'number', e.target.value)}
                    placeholder="eg.: 911"
                    className="font-mono"
                  />
                  <Input
                    value={row.serviceName}
                    onChange={(e) => updateRow(index, 'serviceName', e.target.value)}
                    placeholder="eg.: Police, Firefighters, Ambulance"
                  />
                  <SimpleTooltip content="Remove">
                    <button
                      type="button"
                      onClick={() => deleteRow(index)}
                      aria-label="Remove"
                      className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </button>
                  </SimpleTooltip>
                </div>
              )
            )}

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {t('common:add', 'Add')}
              </button>
            </div>
          </div>
        </FormBox>
      </div>

      <FixedActionBar
        onSave={handleSave}
        onCancel={handleCancel}
        isEditMode={true}
        isSaving={saving}
        cancelText={t('common:cancel')}
        saveDisabled={saving}
      />
    </div>
  );
}
